/**
 * Check-in API. Document check-in HTTP calls.
 * Used by checkinApiSlice; uses ENDPOINTS and getApiClient (not documentsAPI).
 */
import { getApiClient } from "./client";
import { ENDPOINTS } from "../configs/endpoints";
import { logger } from "../../utils/logger";
import UserAuth from "../../auth/userAuth";

/** Default axios client timeout (60s) is too low for sequential bulk check-in. */
const BULK_CHECKIN_TIMEOUT_BASE_MS = 120_000;
const BULK_CHECKIN_TIMEOUT_PER_DOC_MS = 90_000;
const BULK_CHECKIN_TIMEOUT_MAX_MS = 480_000;

/** Share-aligned chunked browse upload settings. */
const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2GB
const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB
const CHUNK_UPLOAD_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes per chunk

export function getBulkCheckinTimeoutMs(documentCount) {
  const count = Math.max(1, Number(documentCount) || 1);
  return Math.min(
    BULK_CHECKIN_TIMEOUT_MAX_MS,
    BULK_CHECKIN_TIMEOUT_BASE_MS + count * BULK_CHECKIN_TIMEOUT_PER_DOC_MS,
  );
}

/**
 * @param {unknown} value
 * @returns {value is Blob}
 */
export function isBrowseFileBlob(value) {
  return typeof Blob !== "undefined" && value instanceof Blob;
}

/**
 * Refresh Azure SSO token (if needed) + silent IFS session before a long chunked upload.
 * @returns {Promise<string|null>} Latest Azure access token (may be unchanged)
 */
async function refreshChunkedUploadAuth() {
  const userAuth = new UserAuth();
  const tokenResponse = await userAuth.getUserToken(true);
  const accessToken = tokenResponse?.accessToken ?? null;

  try {
    const store = (await import("../store/store")).default;
    const { fetchIfsToken, getPendingIfsTokenRefreshPromise } = await import(
      "../slices/login/loginThunks"
    );
    const alreadyRunning = getPendingIfsTokenRefreshPromise?.();
    if (alreadyRunning) {
      await alreadyRunning;
    } else {
      await store
        .dispatch(fetchIfsToken({ refreshToken: true, silent: true }))
        .unwrap();
    }
  } catch (refreshErr) {
    logger.warn(
      "checkinAPI: silent IFS refresh failed before chunked upload",
      refreshErr,
      "API",
    );
  }

  return accessToken;
}

export const checkinAPI = {
  /**
   * Check-in a document (single file) via JSON body (OneDrive fileUrl / small base64).
   * @param {string} accessToken - Bearer token from UserAuth.getUserToken()
   * @param {object} body - Request body for check-in
   * @returns {Promise<{ data: any, status: number }>}
   */
  async checkinDocument(accessToken, body) {
    logger.info("checkinAPI.checkinDocument", { docClass: body?.docClass, title: body?.title }, "API");
    try {
      const client = getApiClient(accessToken);
      const url = ENDPOINTS.CHECKIN.DOCUMENT();
      const response = await client.post(url, body);
      return { data: response.data, status: response.status };
    } catch (err) {
      logger.error("checkinAPI.checkinDocument failed", err, "API");
      throw err;
    }
  },

  /**
   * Share-style chunked binary check-in for local Browse files (supports large files).
   * Refreshes Azure + silent IFS once before upload; User-Key is kept current by the API client interceptor.
   * @param {string} accessToken
   * @param {object} metadata - DocInfoModel fields (fileData omitted / empty)
   * @param {Blob} fileBlob - Raw browsed File/Blob
   * @returns {Promise<{ data: any, status: number }>}
   */
  async checkinDocumentChunked(accessToken, metadata, fileBlob) {
    if (!isBrowseFileBlob(fileBlob)) {
      throw new Error("Browse file data is required for chunked check-in.");
    }
    if (fileBlob.size > MAX_FILE_SIZE) {
      throw new Error(
        `File size exceeds maximum allowed size of ${MAX_FILE_SIZE / (1024 * 1024)}MB`,
      );
    }

    const fileId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const totalChunks = Math.max(1, Math.ceil(fileBlob.size / CHUNK_SIZE));
    const enriched = {
      ...metadata,
      fileData: "",
      fileUrl: undefined,
    };

    logger.info(
      "checkinAPI.checkinDocumentChunked start",
      {
        docClass: enriched?.docClass,
        title: enriched?.title,
        fileSize: fileBlob.size,
        totalChunks,
      },
      "API",
    );

    let bearerToken = accessToken;
    try {
      const refreshed = await refreshChunkedUploadAuth();
      if (refreshed) {
        bearerToken = refreshed;
      }
    } catch (preAuthErr) {
      logger.warn(
        "checkinAPI.checkinDocumentChunked pre-upload auth refresh failed; continuing with provided token",
        preAuthErr,
        "API",
      );
    }

    const client = getApiClient(bearerToken);
    const url = ENDPOINTS.CHECKIN.DOCUMENT_CHUNKED();
    const uploadConfig = {
      timeout: CHUNK_UPLOAD_TIMEOUT_MS,
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
      validateStatus: (status) => status >= 200 && status < 300,
    };

    let lastResponse = null;
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex += 1) {
      const start = chunkIndex * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, fileBlob.size);
      const chunk = fileBlob.slice(start, end);

      const formData = new FormData();
      formData.append(
        "metadata",
        JSON.stringify({
          ...enriched,
          currentChunk: chunkIndex + 1,
          totalChunks,
          fileId,
        }),
      );
      formData.append("chunk", chunk, enriched.fileName || "chunk.bin");
      formData.append("chunkIndex", String(chunkIndex + 1));
      formData.append("totalChunks", String(totalChunks));
      formData.append("fileId", fileId);

      try {
        lastResponse = await client.post(url, formData, uploadConfig);
      } catch (err) {
        logger.error(
          "checkinAPI.checkinDocumentChunked chunk failed",
          {
            chunkIndex: chunkIndex + 1,
            totalChunks,
            error: err,
          },
          "API",
        );
        throw err;
      }
    }

    if (!lastResponse) {
      throw new Error("No response received from chunked check-in.");
    }

    logger.info(
      "checkinAPI.checkinDocumentChunked success",
      { status: lastResponse.status },
      "API",
    );
    return { data: lastResponse.data, status: lastResponse.status };
  },

  /**
   * Check in multiple documents in one request.
   * @param {string} accessToken
   * @param {object[]} body - Array of DocInfoModel payloads
   * @returns {Promise<{ data: any, status: number }>}
   */
  async checkinDocumentsBulk(accessToken, body) {
    logger.info(
      "checkinAPI.checkinDocumentsBulk",
      { count: Array.isArray(body) ? body.length : 0 },
      "API",
    );
    try {
      const client = getApiClient(accessToken);
      const url = ENDPOINTS.CHECKIN.DOCUMENTS_BULK();
      const timeout = getBulkCheckinTimeoutMs(
        Array.isArray(body) ? body.length : 0,
      );
      const response = await client.post(url, body, { timeout });
      return { data: response.data, status: response.status };
    } catch (err) {
      logger.error("checkinAPI.checkinDocumentsBulk failed", err, "API");
      throw err;
    }
  },

  /**
   * Check in a single checked-out document as its existing revision.
   * Mirrors the single-doc `check-in-existing` call used by the Working
   * Documents flow; looped by `checkinExistingDocumentsBulkThunk`.
   * @param {string} accessToken
   * @param {object} body - check-in-existing DocInfo payload
   * @returns {Promise<{ data: any, status: number }>}
   */
  async checkinExistingDocument(accessToken, body) {
    logger.info(
      "checkinAPI.checkinExistingDocument",
      { fileName: body?.fileName },
      "API",
    );
    try {
      const client = getApiClient(accessToken);
      const url = ENDPOINTS.CHECKIN.DOCUMENT_EXISTING();
      const response = await client.post(url, body, {
        timeout: getBulkCheckinTimeoutMs(1),
      });
      return { data: response.data, status: response.status };
    } catch (err) {
      logger.error("checkinAPI.checkinExistingDocument failed", err, "API");
      throw err;
    }
  },
};
